var __decorate = this && this.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
    r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
    d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { forEach, get, has, isEmpty, isPlainObject, isUndefined, omitBy, set, toPath } from "lodash-es";
import { compPathToSourcePath, getCompPathParentInfo, unescapeSlotName } from "../../lib/utils/layout";
import { vueBailDepCollector, vueDeepSet } from "../../lib/utils/vue";
import stringInterop, { deepInterop, isInteropableString, makeEvalFn, tryEval } from "../../lib/utils/stringInterop";
import { isExprObject } from '@tencent/data-schema-core/lib/utils/eval';
import { FactoryState } from './FactoryState';
import { LazyGetter } from '@tencent/data-schema-core';
import PluginRuntime from './LifeCyclePluginRuntime';
import { getDtBus, getDtOption } from "../../lib/comps/DevTool/util/devToolMixin";
import { uuidv4 } from "../../lib/utils";
import { compileInteropableValue } from "../../lib/utils/script/compileInteropableValue";
import { computeFilteredResult } from "../../lib/utils/array";
import { DEFAULT_INLINE_STYLE_PROPS } from "../../lib/utils/consts";
import { convertStyle, needConvertStyle } from "../../lib/ucBus/styleConvertPlugin";
import { getUcEvalPath } from "../../lib/utils/script/evalRuntime";
/**
 * 每一个 Layout 对象会对应生成一个这玩意儿，它提供的 toVNode 方法可以将 Layout 转换为 Vue 的 VNode。
 *
 * 注意：因为存在类似作用域、列表……复用Layout的情况，
 * 因此，这里不存储和页面上下文有关的东西（虽然有 dynamicProps 这种东西存在，但是存储的都是一堆函数，实际取值时还是需要传入当时上下文）。
 *
 * 对于每一个组件，和上下文有关的东西，会存储在 VNodeFactoryContext 上，而 VNodeFactoryContext 又是挂载在实际的渲染上下文（比如 CRenderer）组件上的
 */
export class VNodeFactory {
  /** 静态的 props，和 staticVNodeData.props 是同一个引用 */
  get props() {
    return this.staticVNodeData.props;
  }
  set props(val) {
    this.staticVNodeData.props = val;
  }
  constructor(opts) {
    var _opts$isStyleRoot, _this$scopeRoot, _this$compMeta, _this$compMeta2, _this$compMeta3, _opts$extraPrepareFn;
    /**
     * 这个组件是否含有动态的东西，纯静态节点是可以复用的
     *
     * 在 CompStub#prepare... 里需要修改这个
     */
    this.isDynamic = false;
    /** 每次渲染保持一致的 VNodeData，这个会被浅拷贝 */
    this.staticVNodeData = {};
    /**
     * 每次渲染都要执行的，动态 patch VNode 的方法
     */
    this.dynamicVNodeDataPatch = [];
    /**
     * 执行更靠前的vnodeDataPatch方法
     */
    this.beforeVNodeDataPatch = [];
    /**
     * 每次渲染都要执行的，动态生成 listener
     */
    this.dynamicListeners = {};
    /** 这些 props 是带字符串插值、计算的 */
    this.dynamicProps = {};
    const {
      uc,
      comp,
      compPath = '',
      parent,
      isScopeRoot,
      isPartialRendererRoot
    } = opts;
    this.uc = uc;
    this.isDesignMode = !!this.uc.isDesignMode;
    this.parent = parent;
    this.isScopeRoot = !!isScopeRoot;
    this.isPartialRendererRoot = !!isPartialRendererRoot;
    if (!parent && !isScopeRoot) throw new Error('VNodeFactory must has a parent or get isScopeRoot set');
    this.scopeRoot = this.isScopeRoot ? this : parent.scopeRoot;
    if (this.isScopeRoot && parent && opts.scopedSlotId) this.scopedSlotId = opts.scopedSlotId;
    // style
    this.isStyleRoot = (_opts$isStyleRoot = opts.isStyleRoot) !== null && _opts$isStyleRoot !== void 0 ? _opts$isStyleRoot : this.isScopeRoot;
    if (this.isStyleRoot) {
      this.styleRoot = this;
      this.styledChildren = new Set();
    } else {
      var _parent$styleRoot;
      this.styleRoot = (_parent$styleRoot = parent === null || parent === void 0 ? void 0 : parent.styleRoot) !== null && _parent$styleRoot !== void 0 ? _parent$styleRoot : this.scopeRoot.styleRoot;
    }
    // component
    this.comp = comp;
    this.compPath = compPath;
    if ((_this$scopeRoot = this.scopeRoot) !== null && _this$scopeRoot !== void 0 && _this$scopeRoot.parent) {
      // 在作用域里 就要拼上scopeRoot.parent的sourcePath
      this.sourcePath = [...this.scopeRoot.parent.sourcePath, 'scopedSlots', this.scopeRoot.scopedSlotId, ...compPathToSourcePath(this.compPath)];
    } else {
      // 否则可以直接从compPath拿到sourcePath
      this.sourcePath = compPathToSourcePath(this.compPath);
    }
    this.compMeta = this.uc.components[comp.type];
    this.component = this.isDesignMode && ((_this$compMeta = this.compMeta) === null || _this$compMeta === void 0 ? void 0 : _this$compMeta.componentForDesign) || ((_this$compMeta2 = this.compMeta) === null || _this$compMeta2 === void 0 ? void 0 : _this$compMeta2.component) || comp.type;
    this.pluginRuntime = new PluginRuntime(compPath);
    // 开始准备 render 函数需要的东西。这些步骤可能会相互覆盖一些东西，是正常的
    const compPathInfo = getCompPathParentInfo(compPath);
    const slotId = (compPathInfo === null || compPathInfo === void 0 ? void 0 : compPathInfo.slotId) || 'default';
    this.staticLayoutErrors = [];
    this.staticVNodeData = {
      'uc:type': comp.type,
      'uc:path': compPath,
      'uc:layout': comp,
      'uc:hidden': false,
      'uc:VNodeFactory': this,
      ref: comp.componentId,
      key: comp.componentId || compPath,
      slot: slotId === 'default' ? undefined : unescapeSlotName(slotId),
      on: {},
      props: {
        ...((_this$compMeta3 = this.compMeta) === null || _this$compMeta3 === void 0 ? void 0 : _this$compMeta3.defaultProps),
        ...omitBy(comp.props, isUndefined)
      },
      style: {},
      staticClass: ''
    };
    // ---------------------------
    this.prepareInteropProp();
    this.prepareBindings();
    this.prepareStyles();
    this.prepareFlowEvents();
    this.prepareSlots();
    this.prepareScopedSlots();
    this.prepareDesignerAttrs();
    this.prepareReportAttrs();
    this.prepareReportListeners();
    this.preparePlugin();
    (_opts$extraPrepareFn = opts.extraPrepareFn) === null || _opts$extraPrepareFn === void 0 || _opts$extraPrepareFn.call(opts, this); // optional
    // ---------------------------
    // 静态节点优化
    this.isDynamic = this.isDynamic || this.dynamicVNodeDataPatch.length > 0 || this.beforeVNodeDataPatch.length > 0 || !isEmpty(this.dynamicProps) || !isEmpty(this.dynamicListeners);
    // 修改pagelet会重建vnodeFactory 编辑时没有收益
    this.cacheVnode = this.uc.ucFastMode === 1 && !this.isDesignMode;
    // children的factory会先完成构造函数 isStaticVNodeChildren会逐级上报
    this.isStaticVNodeChildren = !this.children || this.children.length === 0 || this.children.every(children => children.isStaticVNodeChildren && !children.isDynamic);
  }
  /** 注册一个固定的事件回调函数，仅限 prepare 阶段调用 */
  addStaticEventListener(ev, callback) {
    const eventListeners = this.staticVNodeData.on;
    if (!eventListeners[ev]) eventListeners[ev] = [callback];else eventListeners[ev].push(callback);
  }
  /**
   * 注册一个动态的事件回调函数，仅限 prepare 阶段调用
   *
   * 注意，如果 `ev` 是冒号开头的名字，那么会作为 prop 提供到，而不走 Vue2 事件机制
   */
  addDynamicEventListener(ev, callbackFactory) {
    const populateWithDtBusSpy = input => {
      if (ev.startsWith('hook:')) return input;
      return (ctx, state) => {
        const originalFunction = input(ctx, state);
        const {
          renderer
        } = state.ctx;
        const {
          compPath,
          compMeta
        } = this;
        const compInstance = vueBailDepCollector(() => state.ctx.componentInstances[compPath]);
        return function (...args) {
          const shallRecord = getDtOption().recording;
          if (shallRecord) {
            getDtBus().$emit('DevToolEventSpy', {
              args,
              renderer,
              compPath,
              eventId: ev,
              compInstance,
              time: new Date().toLocaleTimeString(),
              name: compMeta === null || compMeta === void 0 ? void 0 : compMeta.name,
              type: compMeta === null || compMeta === void 0 ? void 0 : compMeta.type,
              eid: uuidv4()
            });
          }
          return originalFunction === null || originalFunction === void 0 ? void 0 : originalFunction.apply(this, args);
        };
      };
    };
    const asProp = ev.startsWith(':') && ev.substr(1);
    if (asProp) {
      this.dynamicProps[asProp] = (ctx, state) => populateWithDtBusSpy(callbackFactory)(ctx, state);
      return;
    }
    const eventListeners = this.dynamicListeners;
    if (!eventListeners[ev]) eventListeners[ev] = [populateWithDtBusSpy(callbackFactory)];else eventListeners[ev].push(callbackFactory);
  }
  prepareFactoryStates(ctx) {
    var _state;
    // 先准备 state
    let state = ctx.factoryStates[this.compPath];
    if (((_state = state) === null || _state === void 0 ? void 0 : _state.factory) !== this) {
      var _state2, _state2$destroyed;
      // 如果pagelet改变了 vnodeFactory会销毁 但是对应compPath下的东西还会在 所以要释放旧的$watch
      (_state2 = state) === null || _state2 === void 0 || (_state2$destroyed = _state2.destroyed) === null || _state2$destroyed === void 0 || _state2$destroyed.call(_state2);
      state = new FactoryState(this, ctx, this.staticLayoutErrors, this.pluginRuntime);
      ctx.factoryStates[this.compPath] = state;
      if (this.cacheVnode) {
        // children是静态的 所以在同一个vnodefactory生命周期里可以一直保持同样的缓存策略
        if (this.isStaticVNodeChildren) {
          state.destroyed = ctx.watcher.$watch(() => {
            const vNodeData = this.toVNodeData(ctx);
            return this.toVNodeChild(ctx, vNodeData);
          }, vNode => {
            state.cachedVNode = vNode;
            ctx.emitChange();
          }, {
            immediate: true
          });
        } else {
          state.destroyed = ctx.watcher.$watch(() => this.toVNodeData(ctx), vNodeData => {
            state.cachedVNodeData = vNodeData || {};
            ctx.emitChange();
          }, {
            immediate: true
          });
        }
      }
    }
  }
  toVNode(ctx) {
    this.prepareFactoryStates(ctx);
    let data;
    const state = ctx.factoryStates[this.compPath];
    // 如果一棵树都是static的 那么只缓存树根的vnode即可
    const isStaticRoot = !this.isDynamic && (!this.parent || this.parent.isDynamic);
    if (!this.isDynamic && state.cachedVNode !== undefined) {
      return state.cachedVNode;
    }
    // 1 static节点 并且是root 或者父级是Dynamic
    if (isStaticRoot) {
      data = this.toVNodeData(ctx);
      const vNode = this.toVNodeChild(ctx, data);
      state.cachedVNode = vNode;
      return vNode;
    }
    // 2 static节点 但是父级也是static
    // 3 dynamic节点 但是children全都是static
    // 4 dynamic节点 children也有dynamic的
    if (!this.isDynamic) {
      // 情况2 static 但是不是root 可以不缓存 因为上级会存
      data = this.toVNodeData(ctx);
    } else if (this.cacheVnode) {
      // $watch缓存 情况3
      // 无子factory || 子factory全部静态的时候 全部缓存
      if (this.isStaticVNodeChildren) {
        return state.cachedVNode;
      }
      // $watch缓存data 情况4
      data = state.cachedVNodeData;
      // $createElement里会动到 所以要浅拷贝
      data = {
        ...data
      };
    } else {
      // 旧的全量toVNode 这里会对应情况3 4
      data = this.toVNodeData(ctx);
    }
    return this.toVNodeChild(ctx, data);
  }
  toVNodeData(ctx) {
    const state = ctx.factoryStates[this.compPath];
    // 执行插件的beforeRender
    const beforeRenderPluginResult = this.pluginRuntime.apply('beforeRender', ctx);
    // 由于我们是forEach执行的，所以会导致插件见相同参数的覆盖，这个需要在文档里提示插件开发者
    const dynamicAttrs = {};
    beforeRenderPluginResult.forEach(item => {
      if (!item) return;
      const {
        attrs = {},
        on = {}
      } = item;
      // 设置attrs
      Object.entries(attrs).forEach(([key, value]) => {
        // 如果要覆盖，在window里进行提示
        if (dynamicAttrs[key]) {
          console.warn(`[UI-CORE plugin]正在覆盖元素属性${key}，将要设置为${value}`);
        }
        dynamicAttrs[key] = value;
      });
      // 设置事件
      Object.entries(on).forEach(([k, v]) => {
        this.addStaticEventListener(k, v);
      });
    });
    // 开始渲染
    const data = {
      attrs: dynamicAttrs,
      props: {},
      on: {},
      'uc:renderer': ctx.renderer,
      'uc:VNodeState': state,
      ...this.staticVNodeData,
      hook: {
        create: (_, vnode) => {
          const inst = vnode.componentInstance;
          ctx.setComponentInstance(this.compPath, inst);
        },
        postpatch: (oldVNode, vnode) => {
          const inst = vnode.componentInstance;
          ctx.setComponentInstance(this.compPath, inst);
        },
        destroy: vnode => {
          const inst = vnode.componentInstance;
          if (ctx.componentInstances[this.compPath] === inst) {
            ctx.setComponentInstance(this.compPath, undefined);
          }
        }
      }
    };
    /** 提前计算renderCondition的结果 */
    this.beforeVNodeDataPatch.forEach(patch => {
      patch(ctx, state, data);
    });
    //  不实例化就不用计算相关数据了
    if (data['uc:hidden'] === 'remove' && !this.isDesignMode) {
      return data;
    }
    // 浅层 clone，避免污染到 style 之类的东西
    forEach(data, (v, k) => {
      if (!k.startsWith('uc:') && isPlainObject(v)) {
        data[k] = {
          ...v
        };
      }
    });
    Object.keys(this.dynamicProps).forEach(k => {
      data.props[k] = this.dynamicProps[k](ctx, state);
    });
    this.dynamicVNodeDataPatch.forEach(patch => {
      patch(ctx, state, data);
    });
    forEach(this.dynamicListeners, (fs, key) => {
      if (!data.on[key]) data.on[key] = [];
      fs.forEach(ff => {
        const listener = ff(ctx, state);
        if (typeof listener === 'function') data.on[key].push(listener);
      });
    });
    return data;
  }
  toVNodeChild(ctx, data) {
    var _this$children;
    const keepAlive = data['uc:keepAlive'] && !this.isDesignMode;
    if (data['uc:hidden'] === 'remove' && !this.isDesignMode) {
      if (keepAlive) {
        return ctx.renderer.$createElement('keep-alive', {}, [null]);
      }
      return null;
    }
    const vnode = ctx.renderer.$createElement(this.component, data, (_this$children = this.children) === null || _this$children === void 0 ? void 0 : _this$children.filter(it => it && !it.isPartialRendererRoot).map(x => x.toVNode(ctx)));
    if (keepAlive) {
      return ctx.renderer.$createElement('keep-alive', {}, [vnode]);
    }
    return vnode;
  }
  // ------------------ 以下是若干节点逻辑 ----------------
  prepareInteropProp() {
    const {
      compMeta,
      compPath
    } = this;
    if (!compMeta) return;
    forEach(this.staticVNodeData.props, (rawPropValue, propName) => {
      if (isExprObject(rawPropValue)) {
        this.dynamicProps[propName] = ({
          renderer
        }) => tryEval(rawPropValue.$expr, renderer);
        return;
      }
      if (compMeta.allowInteropProps.includes(propName)) {
        // 字符串的情况，如果不需要插值就直接跳过吧
        if (typeof rawPropValue === 'string' && !isInteropableString(rawPropValue)) return;
        const ucEvalPath = getUcEvalPath(() => `组件:${compMeta.name || compMeta.id} 路径:${compPath}/.props.${propName} 表达式:${rawPropValue}`);
        this.dynamicProps[propName] = ({
          renderer
        }) => deepInterop(rawPropValue, renderer, ucEvalPath);
      }
    });
  }
  prepareBindings() {
    const {
      comp,
      compMeta
    } = this;
    const {
      bindings
    } = comp;
    const bindingMeta = compMeta === null || compMeta === void 0 ? void 0 : compMeta.bindings;
    if (this.isDesignMode && bindingMeta) {
      // 设计模式需要额外的检查
      bindingMeta.forEach(meta => {
        let {
          required
        } = meta;
        if (isExprObject(required)) required = tryEval(required.$expr, {
          ...comp
        });
        if (required && !(bindings !== null && bindings !== void 0 && bindings[meta.id])) {
          this.staticLayoutErrors.push({
            category: 'prop',
            message: `未绑定 ${meta.id}`
          });
        }
      });
    }
    if (!bindings || !bindingMeta) return;
    bindingMeta.forEach(bindingMeta => {
      const bindingId = bindingMeta.id;
      const target = bindings[bindingId];
      if (!target) return;
      // target 是一个形如 `foo.bar` 的字符串，这里拆成 `["foo", "bar"]`
      const targetPathParts = toPath(target);
      if (bindingMeta.prop) {
        this.dynamicProps[bindingMeta.prop] = ({
          renderer
        }) => get(renderer, targetPathParts);
      }
      if (bindingMeta.pathProp) {
        this.dynamicProps[bindingMeta.pathProp] = () => target;
      }
      if (bindingMeta.schemaProp) {
        this.dynamicProps[bindingMeta.schemaProp] = ({
          renderer
        }) => {
          var _renderer$getStubAtPa;
          return (_renderer$getStubAtPa = renderer.getStubAtPath(target)) === null || _renderer$getStubAtPa === void 0 ? void 0 : _renderer$getStubAtPa.schema;
        };
      }
      if (bindingMeta.stubProp) {
        this.dynamicProps[bindingMeta.stubProp] = ({
          renderer
        }) => renderer.getStubAtPath(target);
      }
      if (bindingMeta.event) {
        this.addDynamicEventListener(bindingMeta.event, ({
          renderer
        }) => val => {
          vueDeepSet(renderer, targetPathParts, val);
          // TODO: 后续放在总线管理是否编辑的状态
          const key = 'wContext.isChanged';
          if (!get(renderer, key)) {
            set(renderer, key, true);
          }
        });
      }
      if (this.isDesignMode) {
        // 设计模式下需要做检查
        this.dynamicVNodeDataPatch.push(({
          renderer
        }, state) => {
          const message = `绑定项 ${bindingId} 指向的数据源不存在：${target}`;
          let stub;
          try {
            stub = renderer.getStubAtPath(target);
          } catch (e) {
            // 下面会抛message处理
          }
          const errorKey = `invalidBinding:${bindingId}`;
          if (!stub) {
            state.addLayoutErrors({
              key: errorKey,
              category: 'prop',
              message
            });
          } else {
            state.deleteLayoutError(errorKey);
          }
        });
      }
    });
  }
  prepareStyles() {
    var _mixedStyle, _comp$styleSheetName;
    const {
      comp,
      isDesignMode,
      compMeta,
      uc,
      compPath
    } = this;
    // style
    let mixedStyle = comp.extraStyle;
    const inlineStyleProperties = computeFilteredResult([...DEFAULT_INLINE_STYLE_PROPS, ...get(compMeta, 'inlineStyleProps', []), ...(comp.inlineStyleProps || [])]);
    if (!isEmpty(comp.style)) {
      if (!mixedStyle) mixedStyle = {
        '': {
          '': comp.style
        }
      };else {
        const rootX = mixedStyle[''] || {};
        mixedStyle = {
          ...mixedStyle,
          '': {
            ...rootX,
            '': {
              ...rootX[''],
              ...comp.style
            }
          }
        };
      }
    }
    const ctxBase = {
      uc,
      comp,
      compPath
    };
    // 这部分是老style 需要从里面抽出被设为了inline的样式做处理 inline的样式需要被patch到VNODE上
    if ((_mixedStyle = mixedStyle) !== null && _mixedStyle !== void 0 && (_mixedStyle = _mixedStyle['']) !== null && _mixedStyle !== void 0 && _mixedStyle['']) {
      // {color:red}
      const raw = mixedStyle[''][''];
      // deep copy raw
      const newStatic = {
        ...raw
      };
      // 这一坨挺迷惑的 要共享引用的话 直接下面在raw里delete不就行了? 然后深拷贝出newStatic来用 为什么要先拷贝再塞回去呢?
      mixedStyle = {
        ...mixedStyle,
        '': {
          ...mixedStyle[''],
          '': newStatic
        }
      };
      // inline的 用只包含inline的即可
      const needConvert = needConvertStyle(uc, mixedStyle, ctxBase);
      inlineStyleProperties.forEach(key => {
        if (!has(raw, key)) return;
        // 共享引用 把mixedStyle中inline的部分移除
        delete newStatic[key];
        const value = raw[key];
        if (isInteropableString(value) || needConvert) {
          this.dynamicVNodeDataPatch.push(({
            renderer
          }, state, data) => {
            const finalValue = stringInterop(value, renderer);
            // 普通情况做完插值就可以patch了 提前返回
            if (!needConvert) {
              set(data, ['style', key], finalValue);
              return;
            }
            // needConvert === true 此时有自定义编译样式的需求
            const styleObj = convertStyle(uc, {
              [key]: finalValue
            }, {
              ...ctxBase,
              pseudo: '',
              selector: ''
            });
            // 通过styleObj的方式 可以做到无中生有的添加样式 而不仅仅是改变原有样式
            forEach(styleObj, (value, key) => {
              set(data, ['style', key], value);
            });
          });
        } else {
          this.staticVNodeData.style[key] = value;
        }
      });
      // 全部的属性都是inline了 后面无需再处理
      if (Object.keys(newStatic).length === 0 // no style in stylesheet
      && Object.keys(mixedStyle).length === 1 && Object.keys(mixedStyle['']).length === 1) {
        mixedStyle = undefined;
      }
    }
    // 如果有需要转换的样式 则当成插值表达式进行依赖收集和动态处理 现在的mixedStyle是剔除了inline剩下的部分
    const needConvert = needConvertStyle(uc, mixedStyle, ctxBase);
    const {
      conditionStyle
    } = comp;
    if (!isEmpty(mixedStyle) || !isEmpty(conditionStyle)) {
      this.styleRoot.styledChildren.add(this);
      this.styleKey = `uc-component-${uuidv4()}`;
      this.style = mixedStyle;
      this.staticVNodeData.staticClass += ` ${this.styleKey}`;
      // 如果整个mixedStyle里有一个插值/需要样式转换 整个都扔进dynamicStyleGetter里
      const interopExpr = compileInteropableValue(mixedStyle) || needConvert && JSON.stringify(mixedStyle);
      if (interopExpr) {
        try {
          const styleFn = makeEvalFn(interopExpr, 1, true);
          this.dynamicStyleGetter = interopContext => styleFn.call(interopContext, interopContext) || mixedStyle;
        } catch (e) {
          // cannot compile!
        }
      }
      // conditionStyles
      let conditionStyleExpr = '';
      if (!isEmpty(conditionStyle)) {
        const cssArr = [];
        forEach(conditionStyle, (pseudoClasses, selector) => {
          const selectorArr = [];
          forEach(pseudoClasses, (styles, pseudoClass) => {
            const pseudoClassArr = [];
            if (styles !== null && styles !== void 0 && styles.length) {
              styles.forEach(styleObject => {
                if (styleObject.cond && !isEmpty(styleObject.style)) {
                  var _compileInteropableVa;
                  let styleStr = (_compileInteropableVa = compileInteropableValue(styleObject.style)) !== null && _compileInteropableVa !== void 0 ? _compileInteropableVa : JSON.stringify(styleObject.style);
                  if (selector === '' && pseudoClass === '') {
                    // 行内联样式需要 !important 做覆盖
                    // 在运行时做替换逻辑
                    const procKeys = Object.keys(styleObject.style).filter(k => inlineStyleProperties.has(k));
                    if (procKeys.length) {
                      styleStr = ['(((_$re,_$)=>{', ...procKeys.map(k => {
                        const id = `_$["${k}"]`;
                        return `if (!_$re.test(${id})) ${id} += " !important";`;
                      }), `return _$})(/!important\\s*$/,${styleStr}))`].join('\n');
                    }
                  }
                  pseudoClassArr.push(`...((${styleObject.cond})?${styleStr}:null)`);
                }
              });
            }
            if (pseudoClassArr.length) selectorArr.push(`${JSON.stringify(pseudoClass)}:{${pseudoClassArr.join(',\n')}}`);
          });
          if (selectorArr.length) cssArr.push(`${JSON.stringify(selector)}:{${selectorArr.join(',\n')}}`);
        });
        if (cssArr.length) conditionStyleExpr = `{${cssArr.join(',\n')}}`;
        if (conditionStyleExpr) {
          try {
            const styleFn = makeEvalFn(conditionStyleExpr, 1, true);
            // eslint-disable-next-line max-len
            this.dynamicCondStyleGetter = interopContext => styleFn.call(interopContext, interopContext);
          } catch (e) {
            // cannot compile!
          }
        }
      }
      if (interopExpr || conditionStyleExpr) {
        this.dynamicVNodeDataPatch.push((ctx, state, data) => {
          const {
            ucRendererUUID
          } = ctx.renderer;
          data.attrs['data-uc-renderer-uuid'] = ucRendererUUID;
        });
      }
    }
    // ClassName
    if (isInteropableString(comp.class)) {
      this.dynamicVNodeDataPatch.push(({
        renderer
      }, state, data) => {
        data.class = stringInterop(comp.class, renderer);
      });
    } else if (comp.class) {
      this.staticVNodeData.staticClass += ` ${comp.class}`;
    }
    // styleSheetName
    if ((_comp$styleSheetName = comp.styleSheetName) !== null && _comp$styleSheetName !== void 0 && _comp$styleSheetName.length) {
      this.staticVNodeData.staticClass += ` ${comp.styleSheetName.join(' ')}`;
    }
    // Hidden
    if (comp.displayCondition) {
      this.dynamicVNodeDataPatch.push(({
        renderer
      }, state, data) => {
        const display = tryEval(comp.displayCondition, renderer);
        if (!display) {
          if (!isDesignMode) set(data, ['style', 'display'], 'none');
          data['uc:hidden'] = 'hidden';
        }
      });
    }
    // v-if
    if (comp.renderCondition) {
      this.beforeVNodeDataPatch.push(({
        renderer
      }, state, data) => {
        if (comp.keepAlive) {
          data['uc:keepAlive'] = true;
        }
        const toBeRendered = tryEval(comp.renderCondition, renderer);
        if (!toBeRendered) {
          data['uc:hidden'] = 'remove';
        }
      });
    }
  }
  prepareFlowEvents() {
    const {
      comp: layout,
      compMeta: meta
    } = this;
    if (!meta || !layout.events) return;
    Object.keys(layout.events).forEach(eventKey => {
      // 由于事件钩子依赖于 ctx 和 state，所以要动态获取，按需生成
      // 生成的逻辑在 State 的 getFlowState 方法里面
      const getEventListener = (ctx, state) => {
        var _state$getFlowState;
        return (_state$getFlowState = state.getFlowState(eventKey)) === null || _state$getFlowState === void 0 ? void 0 : _state$getFlowState.eventListener;
      };
      this.addDynamicEventListener(eventKey, getEventListener);
    });
  }
  /**
   * 由 meta 和 Layout 里的静态配置生成的插槽列表。
   *
   * 包括了 scopedSlots 和普通 slots
   *
   * 注意，由于 <uc-slot /> 的动态方案，这里的列表不全！！！
   * 请使用 vNodeFactoryState 的 getNormalSlots() 获取完整的 **普通插槽** 列表，以及用它去搜索对应的 DOM 节点
   */
  get slotMetas() {
    var _this$compMeta4;
    return (_this$compMeta4 = this.compMeta) === null || _this$compMeta4 === void 0 ? void 0 : _this$compMeta4.getSlots(this);
  }
  prepareSlots() {
    const {
      uc,
      comp: layout
    } = this;
    const slotComps = {
      ...layout.slots,
      default: layout.children
    };
    // 【已去掉】根据插槽的默认配置，补全插槽内容
    //
    // 由于存在“空插槽”逻辑上冲突的问题（Layout 不保存空数组，以及这里补全的东西在编辑区里无法选中），
    // 所以这个自动补全 default 的能力暂时去掉了。等以后有空再看怎么优化
    //
    // this.slotMetas?.forEach((slotMeta) => {
    //   if (slotMeta.type !== 'normal') return;
    //   if (slotComps[slotMeta.id]) return;
    //   if (!slotMeta.default) return;
    //
    //   slotComps[slotMeta.id] = slotMeta.default;
    // });
    // 然后开始生成子 VNodeFactory
    const ans = [];
    forEach(slotComps, (comps, slotId) => {
      if (!comps) return;
      comps.forEach((child, index) => {
        if (!child || typeof child !== 'object' || !child.type) return; // 不知道用户侧怎么产生的 null
        const stub = new VNodeFactory({
          uc,
          comp: child,
          compPath: `${this.compPath}/${slotId}/${index}`,
          parent: this
        });
        if (stub.isDynamic) this.isDynamic = true;
        ans.push(stub);
      });
    });
    this.children = ans;
  }
  get scopedSlotMetas() {
    var _this$slotMetas;
    return (_this$slotMetas = this.slotMetas) === null || _this$slotMetas === void 0 ? void 0 : _this$slotMetas.filter(x => x.type === 'scoped');
  }
  prepareScopedSlots() {
    const slots = this.scopedSlotMetas;
    if (!(slots !== null && slots !== void 0 && slots.length)) return;
    // 每次渲染时都需要填入 scopedSlots
    this.dynamicVNodeDataPatch.push((ctx, state, data) => {
      data.scopedSlots = {
        ...data.scopedSlots,
        ...state.scopedSlots
      };
    });
  }
  prepareDesignerAttrs() {
    const {
      isDesignMode,
      comp: layout,
      compPath,
      sourcePath
    } = this;
    if (!isDesignMode) return;
    const designer = this.uc.designer;
    this.dynamicVNodeDataPatch.push((ctx, state, data) => {
      var _designer$renderer;
      const {
        ucRendererUUID
      } = ctx.renderer;
      // eslint-disable-next-line max-len
      const isCurrentRenderer = ((_designer$renderer = designer.renderer) === null || _designer$renderer === void 0 ? void 0 : _designer$renderer.ucRendererUUID) === ucRendererUUID && designer.renderer.ucVNodeFactoryContext === ctx;
      const designModeSelectedCompPath = isCurrentRenderer && designer.compPath;
      Object.assign(data.attrs, {
        'data-uc-type': layout.type,
        'data-uc-selected': compPath && compPath === designModeSelectedCompPath ? true : undefined,
        'data-uc-path': compPath,
        'data-uc-sourcePath': sourcePath.join('/'),
        'data-uc-design-mode': true,
        'data-uc-display-condition': layout.displayCondition || undefined,
        'data-uc-hidden': !!data['uc:hidden'],
        'data-uc-renderer-uuid': ucRendererUUID
      });
      if (!data.nativeOn) data.nativeOn = {};
      if (isCurrentRenderer) {
        data.nativeOn['!mousemove'] = ev => {
          designer.editZoneState.handleComponentMouseMoveCapture(ev, compPath);
        };
      }
    });
  }
  /**
   * 监听组件自主上报
   */
  prepareReportListeners() {
    var _compMeta$report;
    const {
      comp: layout,
      compPath,
      compMeta
    } = this;
    if (!(compMeta !== null && compMeta !== void 0 && (_compMeta$report = compMeta.report) !== null && _compMeta$report !== void 0 && _compMeta$report.selfReport)) return;
    const getDimObj = (dimensions, renderer, $event) => {
      const ret = {};
      dimensions.filter(({
        id,
        value
      }) => id && value).forEach(({
        id,
        value
      }) => {
        ret[id] = tryEval(value, renderer, {
          $event
        });
      });
      return ret;
    };
    const getReportInfo = (ctx, reportValue, $event) => {
      if (!reportValue) return {
        eid: '',
        dimensions: {}
      };
      const {
        renderer
      } = ctx;
      const {
        dimensions = [],
        eidInfo
      } = reportValue;
      return {
        eid: tryEval((eidInfo === null || eidInfo === void 0 ? void 0 : eidInfo.value) || '', renderer, {
          $event
        }),
        dimensions: getDimObj(dimensions, renderer, $event)
      };
    };
    this.addDynamicEventListener('uc:report', ctx => msg => {
      var _layout$reports;
      const {
        event,
        eid: paramEid,
        $event
      } = msg || {};
      const {
        eid,
        dimensions
      } = getReportInfo(ctx, (_layout$reports = layout.reports) === null || _layout$reports === void 0 ? void 0 : _layout$reports[event], $event);
      this.uc.root.$emit('uc:report', {
        event,
        id: layout.componentId,
        path: compPath,
        eid: eid || paramEid || layout.componentId,
        dimensions
      });
    });
    this.dynamicVNodeDataPatch.push((ctx, state, data) => {
      // 标签识别是否内部自主上报
      Object.assign(data.attrs, {
        'data-uc-report-self': 1
      });
    });
  }
  // 加载注册组件
  preparePlugin() {
    const {
      componentPlugins
    } = this.uc;
    // 收集需要注册的插件
    const usedPluginKeys = new Set();
    // 首先是全局开启的插件
    Object.values(componentPlugins).forEach(pluginConfig => {
      if (pluginConfig.global) {
        usedPluginKeys.add(pluginConfig.id);
      }
    });
    // 然后是这个组件注册的插件
    const {
      plugins = {}
    } = this.comp;
    Object.keys(plugins).forEach(plugin => {
      usedPluginKeys.add(plugin);
    });
    // 根据收集到插件key，注册插件
    usedPluginKeys.forEach(key => {
      // 获取pluginConfig
      const pluginConfig = componentPlugins[key];
      // 获取plugin数据
      const pluginData = plugins[key];
      // 注册组件
      if (pluginConfig) {
        var _pluginConfig$modes;
        // 根据当前的模式，决定是否要执行插件
        const mode = this.isDesignMode ? 'design' : 'runtime';
        // 如果设置了模式，则只有指定模式下才执行
        if (pluginConfig.modes && !((_pluginConfig$modes = pluginConfig.modes) !== null && _pluginConfig$modes !== void 0 && _pluginConfig$modes.includes(mode))) {
          return;
        }
        this.pluginRuntime.use(key, pluginData, pluginConfig);
      }
    });
    this.addDynamicEventListener('hook:created', (ctx, state) => () => {
      if (state.pluginRuntime) {
        state.pluginRuntime.apply('created', ctx);
      }
    });
    this.addDynamicEventListener('hook:mounted', (ctx, state) => () => {
      if (state.pluginRuntime) {
        state.pluginRuntime.apply('mounted', ctx);
      }
    });
    this.addDynamicEventListener('hook:updated', (ctx, state) => () => {
      if (state.pluginRuntime) {
        state.pluginRuntime.apply('updated', ctx);
      }
    });
    this.addDynamicEventListener('hook:beforeDestroy', (ctx, state) => () => {
      if (state.pluginRuntime) {
        state.pluginRuntime.apply('beforeDestroy', ctx);
      }
    });
  }
  prepareReportAttrs() {
    const {
      comp: layout,
      compPath
    } = this;
    // 把参数转为querystring的形式
    const stringify = (dimensions, renderer) => dimensions.filter(({
      id
    }) => id).map(({
      id,
      value
    }) => `${id}=${encodeURIComponent(tryEval(value, renderer))}`).join('&');
    // 根节点，挂载公参
    if (!this.parent) {
      this.dynamicVNodeDataPatch.push((ctx, state, data) => {
        var _ctx$renderer;
        const publicDims = (_ctx$renderer = ctx.renderer) === null || _ctx$renderer === void 0 || (_ctx$renderer = _ctx$renderer.ucPagelet) === null || _ctx$renderer === void 0 || (_ctx$renderer = _ctx$renderer.reportDimensions) === null || _ctx$renderer === void 0 ? void 0 : _ctx$renderer.filter(({
          public: p,
          id,
          value
        }) => p && id && value);
        if (!(publicDims !== null && publicDims !== void 0 && publicDims.length)) return;
        Object.assign(data.attrs, {
          'data-uc-report-public-dimensions': stringify(publicDims, ctx.renderer)
        });
      });
    }
    if (!layout.reports || !Object.keys(layout.reports)) return;
    const eventAttrs = {
      click: {
        eid: 'data-uc-report-click-eid',
        value: 'data-uc-report-click-value'
      },
      onview: {
        eid: 'data-uc-report-onview-eid',
        value: 'data-uc-report-onview-value'
      }
    };
    const setAttrs = (event, ctx, reportValue) => {
      const {
        renderer
      } = ctx;
      const {
        dimensions,
        eidInfo
      } = reportValue;
      if (!dimensions) return;
      return {
        'data-uc-report-id': layout.componentId,
        'data-uc-report-path': compPath,
        [eventAttrs[event].value]: stringify(dimensions, renderer),
        [eventAttrs[event].eid]: tryEval((eidInfo === null || eidInfo === void 0 ? void 0 : eidInfo.value) || '', renderer) || layout.componentId
      };
    };
    Object.entries(layout.reports).forEach(([key, reportValue]) => {
      if (!eventAttrs[key]) return;
      this.dynamicVNodeDataPatch.push((ctx, state, data) => {
        // 需要默认挂载
        Object.assign(data.attrs, {
          ...setAttrs(key, ctx, reportValue)
        });
      });
    });
  }
}
__decorate([LazyGetter()], VNodeFactory.prototype, "slotMetas", null);
__decorate([LazyGetter()], VNodeFactory.prototype, "scopedSlotMetas", null);
//# sourceMappingURL=VNodeFactory.js.map
